/**
 * HR Audit Log — Admin → HR → HR Audit Log  (/admin/hr/audit-log[?employee_id=…&subject_type=hr|user])
 * HR-AUDIT-UI-001 (Sep 2026): premium restyle on the shared ActivityTimeline; deep-link query params.
 * Data/behaviour unchanged: GET /api/hr/audit-log (hr_audit_log + activity_events pivot), 50/page,
 * employee/action/source filters, RBAC on the endpoint. V1: timestamps in America/New_York (ET).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FileText, ChevronLeft, ChevronRight, Loader2, Search, ScrollText, X, UserCircle2, Coffee, Server, Scale, Eye } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { formatDateTimeET } from '../../../utils/formatters';
import { ActivityTimeline } from './workspace/ActivityTimeline';

const API = process.env.REACT_APP_BACKEND_URL || '';
const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const HR_ACTIONS = [
  ['create', 'Created'], ['update', 'Updated'], ['status_change', 'Status Change'], ['config_update', 'Config Update'],
  ['employee_id_changed', 'Employee ID Changed'], ['work_email_changed', 'Work Email Changed'], ['shift_update', 'Shift Updated'],
  ['user_account_linked', 'Account Linked'], ['user_account_relinked', 'Account Re-linked'], ['user_account_unlinked', 'Account Unlinked'], ['bulk_link_user', 'Bulk Account Link'],
  ['leave_balance_adjustment', 'Leave Balance Adjustment'], ['sensitive_view', 'Sensitive Data Viewed'],
  ['export', 'Bulk Export'], ['import', 'Bulk Import'], ['import_create', 'Imported (Created)'], ['import_update', 'Imported (Updated)'],
  ['it_offboarding_triggered', 'IT Offboarding Triggered'], ['it_offboarding_cancelled', 'IT Offboarding Cancelled'], ['it_clearance_completed', 'IT Clearance Completed'],
  ['it_asset_recovery_escalated', 'Asset Recovery Escalated'], ['it_asset_recovery_resolved', 'Asset Recovery Resolved'],
];
const USER_ACTIONS = [['started', 'Break Started'], ['ended', 'Break Ended'], ['auto_ended', 'Auto-Ended'], ['force_ended', 'Force-Ended'], ['acknowledged', 'Acknowledged'], ['reminder_sent', 'Reminder Sent']];

const LEGEND = [
  { icon: Server, cls: 'bg-slate-100 text-slate-600', label: 'System · all employees', desc: 'Bulk import/export operations' },
  { icon: Scale, cls: 'bg-indigo-50 text-indigo-700', label: 'User account', desc: 'Leave balance adjustments (keyed by account)' },
  { icon: Eye, cls: 'bg-amber-50 text-amber-700', label: 'Access log', desc: 'Sensitive fields viewed — nothing changed' },
  { icon: Coffee, cls: 'bg-emerald-50 text-emerald-700', label: 'User activity', desc: 'Break events (switch Source to view)' },
];

const HRAuditLog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  // Filters — deep-link aware (?employee_id=EMP-001&subject_type=user)
  const [employeeIdFilter, setEmployeeIdFilter] = useState(searchParams.get('employee_id') || '');
  const [actionFilter, setActionFilter] = useState(searchParams.get('action') || '');
  const [subjectType, setSubjectType] = useState(searchParams.get('subject_type') === 'user' ? 'user' : 'hr');
  const limit = 50;

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit);
      if (employeeIdFilter) params.append('employee_id', employeeIdFilter);
      if (actionFilter) params.append('action', actionFilter);
      if (subjectType === 'user') params.append('subject_type', 'user');
      const response = await axios.get(`${API}/api/hr/audit-log?${params.toString()}`, { headers: getAuthHeaders() });
      setLogs(response.data.data || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.pages || 1);
    } catch (err) {
      console.error('Error fetching audit logs:', err);
    } finally {
      setLoading(false);
    }
  }, [page, employeeIdFilter, actionFilter, subjectType]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // keep the URL shareable
  useEffect(() => {
    const next = new URLSearchParams();
    if (employeeIdFilter) next.set('employee_id', employeeIdFilter);
    if (actionFilter) next.set('action', actionFilter);
    if (subjectType === 'user') next.set('subject_type', 'user');
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [employeeIdFilter, actionFilter, subjectType]); // eslint-disable-line react-hooks/exhaustive-deps

  const clear = () => { setEmployeeIdFilter(''); setActionFilter(''); setPage(1); };
  const deepLinked = !!searchParams.get('employee_id') && employeeIdFilter === searchParams.get('employee_id') && subjectType === 'hr';

  return (
    <div className="space-y-4 max-w-[1500px]" data-testid="hr-audit-log">
      <section className="lv-card ews-hero an-rise relative overflow-hidden px-5 py-4 flex flex-wrap items-center gap-4">
        <span className="h-12 w-12 shrink-0 rounded-2xl bg-violet-50 text-violet-600 flex items-center justify-center"><ScrollText className="h-6 w-6" /></span>
        <div className="min-w-[220px] flex-1">
          <h1 className="crm-page-title">HR Audit Log</h1>
          <p className="text-[13px] text-slate-500 mt-0.5">Permanent, cross-employee record of HR changes for compliance and audit — {total.toLocaleString()} entries{subjectType === 'user' ? ' (user activity)' : ''}</p>
        </div>
        <div className="hidden lg:flex flex-wrap gap-2" data-testid="audit-legend">
          {LEGEND.map(l => { const Icon = l.icon; return <span key={l.label} title={l.desc} className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11.5px] font-semibold ${l.cls}`}><Icon className="h-3.5 w-3.5" />{l.label}</span>; })}
        </div>
      </section>

      <section className="lv-card an-rise p-4" style={{ animationDelay: '60ms' }} data-testid="audit-filters">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input className="ews-control h-10 pl-9 rounded-lg border-slate-200 focus-visible:ring-violet-200"
              placeholder={subjectType === 'user' ? 'Filter by User ID or Name' : 'Filter by Employee ID (e.g., EMP-001)'}
              value={employeeIdFilter} onChange={(e) => { setEmployeeIdFilter(e.target.value); setPage(1); }} data-testid="audit-employee-filter" />
          </div>
          <Select value={subjectType} onValueChange={(v) => { setSubjectType(v); setActionFilter(''); setPage(1); }}>
            <SelectTrigger className="w-[210px] h-10 rounded-lg border-slate-200" data-testid="audit-subject-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="hr">HR Records</SelectItem>
              <SelectItem value="user">User Activity (Breaks)</SelectItem>
            </SelectContent>
          </Select>
          <Select value={actionFilter || 'all'} onValueChange={(v) => { setActionFilter(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-[220px] h-10 rounded-lg border-slate-200" data-testid="audit-action-filter"><SelectValue placeholder="All Actions" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              {(subjectType === 'user' ? USER_ACTIONS : HR_ACTIONS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={clear} className="an-btn h-10 rounded-lg gap-1.5" data-testid="audit-clear-btn"><X className="h-3.5 w-3.5" /> Clear</Button>
        </div>
        {deepLinked && (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-violet-100 bg-violet-50/60 px-3 py-2 text-[12.5px] text-violet-800" data-testid="audit-employee-context">
            <UserCircle2 className="h-4 w-4 text-violet-500" /> Showing the complete history for employee <strong className="font-mono">{employeeIdFilter}</strong>
            <Link to={`/admin/hr/employees/${employeeIdFilter}`} className="ml-auto font-semibold hover:underline" data-testid="audit-back-to-profile">Open profile →</Link>
          </div>
        )}
      </section>

      <div className="flex items-center justify-between text-[13px] text-slate-500" data-testid="audit-summary-line">
        <p>Showing <span className="font-semibold text-slate-800">{logs.length}</span> of <span className="font-semibold text-slate-800">{total}</span> entries · all times ET</p>
        {totalPages > 1 && <p>Page <span className="font-semibold text-slate-800">{page}</span> of <span className="font-semibold text-slate-800">{totalPages}</span></p>}
      </div>

      <section className="lv-card an-rise p-5" style={{ animationDelay: '120ms' }} data-testid="audit-log-list">
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-violet-500" /></div>
        ) : logs.length === 0 ? (
          <div className="text-center py-16" data-testid="audit-empty">
            <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4"><FileText className="w-7 h-7 text-slate-400" /></div>
            <h4 className="text-[15px] font-semibold text-slate-700 mb-1">No audit entries found</h4>
            <p className="text-[13px] text-slate-500 max-w-sm mx-auto">Entries appear here when employee records are created or modified. Try clearing the filters.</p>
          </div>
        ) : (
          <ActivityTimeline rows={logs} testid="audit-timeline" showSubject formatTime={formatDateTimeET} />
        )}
        {totalPages > 1 && (
          <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between" data-testid="audit-pagination">
            <p className="text-[13px] text-slate-500">Page <span className="font-semibold text-slate-800">{page}</span> of <span className="font-semibold text-slate-800">{totalPages}</span></p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="an-btn h-9 rounded-lg" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} data-testid="audit-prev-page"><ChevronLeft className="w-4 h-4" /> Prev</Button>
              <Button variant="outline" size="sm" className="an-btn h-9 rounded-lg" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} data-testid="audit-next-page">Next <ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default HRAuditLog;
